import { useState } from "react";
import { jobService } from "../../utils/jobService";

export default function JobCard({ job, onClick }) {
  const [showAllRequirements, setShowAllRequirements] = useState(false);
  
  // Map API fields to display fields
  const jobTitle = job.position_title || job.public_job_title || job.title || "Untitled Position";
  const postedDate = job.created || job.modified || job.posted_date;
  const formattedDate = postedDate ? jobService.formatDateForDisplay(postedDate) : null;
  
  // Build location string
  const locationParts = [];
  if (job.city) locationParts.push(job.city);
  if (job.state) locationParts.push(job.state);
  if (job.country) locationParts.push(job.country);
  const location = locationParts.length > 0 ? locationParts.join(", ") : null;
  
  // Extract job description - prefer public_job_desc, then requisition_description
  const jobDescription = job.public_job_desc || job.requisition_description || job.description || "";
  
  // Extract key requirements from description (bullet points)
  const extractRequirements = (html) => {
    if (!html) return [];
    const requirements = [];
    
    // Use regex to extract list items from HTML
    const liRegex = /<li[^>]*>(.*?)<\/li>/gi;
    let match;
    let count = 0;
    
    while ((match = liRegex.exec(html)) !== null) {
      // Remove HTML tags from the text
      const text = match[1]
        .replace(/<[^>]+>/g, '') // Remove HTML tags
        .replace(/&nbsp;/g, ' ') // Replace &nbsp; with space
        .replace(/&amp;/g, '&') // Replace &amp; with &
        .replace(/&lt;/g, '<') // Replace &lt; with <
        .replace(/&gt;/g, '>') // Replace &gt; with >
        .trim();
      
      if (text && text.length > 10) { // Only add if meaningful text
        requirements.push(text);
        count++;
      }
    }
    
    // If no list items found, try to extract from paragraph text
    if (requirements.length === 0) {
      const pRegex = /<p[^>]*>(.*?)<\/p>/gi;
      while ((match = pRegex.exec(html)) !== null && count < 5) {
        const text = match[1]
          .replace(/<[^>]+>/g, '')
          .replace(/&nbsp;/g, ' ')
          .trim();
        if (text && text.length > 20 && !text.includes('Thanks') && !text.includes('Regards')) {
          requirements.push(text.substring(0, 150) + (text.length > 150 ? '...' : ''));
          count++;
        }
      }
    }
    
    return requirements;
  };
  
  const allRequirements = extractRequirements(jobDescription);
  // Show only first 2 requirements initially
  const displayedRequirements = showAllRequirements ? allRequirements : allRequirements.slice(0, 2);
  const hasMoreRequirements = allRequirements.length > 2;

  return (
    <div className="job-card" onClick={onClick}>
      <div className="job-card__grid-bg"></div>
      
      <div className="job-card__content">
        {/* Job Title Section */}
        <div className="job-card__title-section">
          <label className="job-card__label">Job Title</label>
          <h3 className="job-card__title">{jobTitle}</h3>
        </div>

        {/* Posted Date and Location */}
        <div className="job-card__info-row">
          {formattedDate && (
            <div className="job-card__info-item">
              <label className="job-card__label">Posted Date</label>
              <span className="job-card__value">{formattedDate}</span>
            </div>
          )}
          {location && (
            <div className="job-card__info-item">
              <label className="job-card__label">Location</label>
              <span className="job-card__value">{location}</span>
            </div>
          )}
        </div>

        {/* Job Description Section */}
        {displayedRequirements.length > 0 && (
          <div className="job-card__description-section">
            <h4 className="job-card__description-title">Job description</h4>
            <ul className="job-card__requirements">
              {displayedRequirements.map((req, index) => (
                <li key={index} className="job-card__requirement">
                  <span className="job-card__checkmark">✓</span>
                  <span>{req}</span>
                </li>
              ))}
            </ul>
            {hasMoreRequirements && !showAllRequirements && (
              <button 
                className="job-card__read-more"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllRequirements(true);
                }}
              >
                Read more →
              </button>
            )}
            {hasMoreRequirements && showAllRequirements && (
              <button 
                className="job-card__read-less"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllRequirements(false);
                }}
              >
                Read less ↑
              </button>
            )}
          </div>
        )}

        {/* Apply Button */}
        <div className="job-card__footer">
          <button className="job-card__apply-btn" onClick={(e) => {
            e.stopPropagation();
            onClick(job);
          }}>
            Apply Now
            <span className="job-card__arrow">→</span>
          </button>
        </div>
      </div>
    </div>
  );
}

